import React, { useEffect, useMemo, useState } from 'react';
import { Divider, Select } from 'antd';
import { CloseRounded } from '@mui/icons-material';
import GenericDialog from '@common/GenericDialog';
import Button from '@common/Button';
import PopConfirm from '@common/PopConfirm';
import CircularLoader from '@common/CircularLoader';
import axios from '@common/axios';

const EditClassesDialog = ({ scheduleId, visible, handleClose, plan, onSuccess }) => {
    const [localClassIds, setLocalClassIds] = useState(new Set());
    const [allClasses, setAllClasses] = useState([]);
    const [loadingClasses, setLoadingClasses] = useState(false);
    const [selectedClassId, setSelectedClassId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (visible) {
            setLocalClassIds(new Set(plan?.classIds ?? []));
            setSelectedClassId(null);
            setHasChanges(false);
            setLoadingClasses(true);
            axios.get('/web/ui/sc/classes')
                .then(r => setAllClasses(Object.values(r.data)))
                .finally(() => setLoadingClasses(false));
        }
    }, [visible]);

    const closeDialog = () => {
        if (hasChanges) onSuccess();
        handleClose();
    };

    const syncClasses = async (ids) => {
        setIsSaving(true);
        setHasChanges(true);
        try {
            await axios.post(`/web/ui/lessonScheduler/plans/${scheduleId}/classes`, {
                classIds: [...ids],
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemove = (classId) => {
        const className = allClasses.find(c => c.classId === classId)?.className;
        const next = new Set(localClassIds);
        next.delete(classId);
        setLocalClassIds(next);
        syncClasses(next).then(() => {
            displayToastMessage('Class removed', `${className} has been removed.`, 'success');
        });
    };

    const handleAdd = () => {
        if (!selectedClassId) return;
        const next = new Set(localClassIds);
        next.add(selectedClassId);
        setLocalClassIds(next);
        setSelectedClassId(null);
        syncClasses(next);
    };

    const assignedClasses = useMemo(
        () => allClasses.filter(c => localClassIds.has(c.classId)),
        [allClasses, localClassIds]
    );

    const addOptions = useMemo(
        () => allClasses
            .filter(c => !localClassIds.has(c.classId))
            .map(c => ({ label: c.className, value: c.classId })),
        [allClasses, localClassIds]
    );

    return (
        <GenericDialog
            title="Edit classes"
            open={visible}
            onClose={closeDialog}
            dialogWidth={500}
            buttons={[
                { name: 'Done', callback: closeDialog, extraProps: { type: 'primary' } },
            ]}
        >
            <p className="ch-label">Assigned classes</p>
            {assignedClasses.length === 0 ? (
                <p className="ch-lesson-planner-edit-classes-empty">No classes assigned yet.</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {assignedClasses.map(c => (
                        <div key={c.classId} className="ch-lesson-planner-edit-classes-class-row">
                            <div className="ch-lesson-planner-edit-classes-class-info">
                                <span className="ch-lesson-planner-edit-classes-class-name">{c.className}</span>
                                {c.schoolYearDescription && (
                                    <span className="ch-lesson-planner-edit-classes-year">{c.schoolYearDescription}</span>
                                )}
                            </div>
                            <PopConfirm
                                title="Remove this class?"
                                description="This will remove it from the plan and delete any lesson statuses associated with this class."
                                onConfirm={() => handleRemove(c.classId)}
                                okText="Remove"
                                cancelText="Cancel"
                                placement="left"
                                overlayStyle={{ maxWidth: '220px' }}
                            >
                                <Button type="text" icon={<CloseRounded />}>Remove</Button>
                            </PopConfirm>
                        </div>
                    ))}
                </div>
            )}

            <Divider className="ch-divider" />

            <p className="ch-label">Add a class</p>
            {loadingClasses ? (
                <CircularLoader />
            ) : (
                <div className="ch-lesson-planner-edit-classes-add-row">
                    <Select
                        style={{ flex: 1 }}
                        options={addOptions}
                        value={selectedClassId}
                        onChange={setSelectedClassId}
                        placeholder="Select a class..."
                        showSearch
                        filterOption={(input, option) =>
                            option.label.toLowerCase().includes(input.toLowerCase())
                        }
                    />
                    <Button
                        type="primary"
                        onClick={handleAdd}
                        disabled={!selectedClassId}
                        loading={isSaving}
                    >
                        Add
                    </Button>
                </div>
            )}
            <Divider className="ch-divider" />
        </GenericDialog>
    );
};

export default EditClassesDialog;
